// =============================================================================
// Histórico de Movimentações — quem fez o quê, quando, e o que era antes
//
// A CAPACIDADE existe desde a 004 (append-only, trigger no banco); esta é a
// tela de consulta que o §5.1 pede e a Frente 13 vai aprofundar. A RLS entrega
// o Histórico a `financeiro` e `admin`, e mais ninguém.
//
// "Reverter" aparece só para admin, e só nas ações que o banco sabe desfazer de
// verdade (migration 035): pendência tratada, alocação confirmada ou realocada.
// Quem decide é a coluna `reversivel` da view, não uma lista aqui — e a RPC
// confere de novo na hora, recusando se o registro mudou depois da ação.
// Orçamento e acessos não se revertem daqui: corrige-se pela tela deles, e a
// correção fica neste Histórico do mesmo jeito.
// =============================================================================

import { useState, type ReactNode } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  api,
  buscarMovimentacoes,
  FalhaApi,
  reverterMovimentacao,
  type FiltroHistorico,
  type Movimentacao,
} from "../lib/api.ts";
import { moeda } from "../lib/formato.ts";
import { Painel, Pilula, Vazio } from "../componentes/ui.tsx";

const POR_PAGINA = 50;

const DATA_HORA_SP = new Intl.DateTimeFormat("pt-BR", {
  timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", year: "numeric",
  hour: "2-digit", minute: "2-digit",
});

/** Nome de cada ação para quem lê. Ação nova sem nome aparece crua, não some. */
const ROTULO_ACAO: Record<string, string> = {
  orcamento_importado: "Orçamento importado",
  orcamento_editado_na_planilha: "Orçamento editado na planilha",
  orcamento_linhas_excluidas: "Linhas do orçamento excluídas",
  linha_criada: "Linha orçamentária criada",
  linha_alterada: "Linha orçamentária alterada",
  alocacao_confirmada: "Lançamento confirmado numa linha",
  alocacao_realocada: "Lançamento trocado de linha",
  alocacao_aplicada_aos_iguais: "Decisão aplicada aos iguais",
  categoria_fora_do_orcamento: "Categoria tirada do orçamento",
  categoria_de_volta_ao_orcamento: "Categoria de volta ao orçamento",
  renovacao_definida: "Renovação de contrato informada",
  renovacao_removida: "Renovação manual tirada do contrato",
  subcategoria_renomeada: "Subcategoria renomeada",
  limiares_definidos: "Limites de alerta ajustados",
  limiares_padrao_alterados: "Padrão dos limites de alerta alterado",
  reconciliacao: "Matching rodou",
  competencia_ajustada_pela_descricao: "Competência ajustada pela descrição",
  competencia_mudou: "Lançamento mudou de mês no Conta Azul",
  competencia_ajustada_manualmente: "Competência ajustada à mão",
  status_corrigido: "Status corrigido pelo admin",
  excecao_em_tratativa: "Pendência em tratativa",
  excecao_resolvida: "Pendência resolvida",
  excecoes_resolvidas_em_lote: "Pendências resolvidas em lote",
  excecoes_fechadas_pela_sincronizacao: "Pendências fechadas pela sincronização",
  justificativa_registrada: "Justificativa do gestor",
  revisao_solicitada: "Revisão de orçamento solicitada",
  remanejamento_solicitado: "Remanejamento solicitado",
  solicitacao_aprovada: "Solicitação aprovada pelo Financeiro",
  solicitacao_recusada: "Solicitação recusada pelo Financeiro",
  gestor_criado: "Gestor cadastrado",
  gestor_alterado: "Gestor alterado",
  credencial_criada: "Conta Azul conectado",
  credencial_rotacionada: "Token do Conta Azul renovado",
  cadastros_atualizados_do_conta_azul: "Cadastros atualizados do Conta Azul",
  reversao: "Reversão",
};

/** Grupos do filtro. "Todas" é a ausência de filtro, não um grupo. */
const GRUPOS: { id: string; rotulo: string; acoes?: string[] }[] = [
  { id: "todas", rotulo: "Todas" },
  {
    id: "orcamento", rotulo: "Orçamento",
    acoes: ["orcamento_importado", "orcamento_editado_na_planilha", "orcamento_linhas_excluidas", "linha_criada", "linha_alterada",
            "categoria_fora_do_orcamento", "categoria_de_volta_ao_orcamento", "renovacao_definida", "renovacao_removida",
            "limiares_definidos", "limiares_padrao_alterados", "subcategoria_renomeada"],
  },
  {
    id: "pendencias", rotulo: "Pendências",
    acoes: ["excecao_em_tratativa", "excecao_resolvida", "excecoes_resolvidas_em_lote", "excecoes_fechadas_pela_sincronizacao"],
  },
  { id: "gestor", rotulo: "Justificativas e revisões", acoes: ["justificativa_registrada", "revisao_solicitada", "remanejamento_solicitado", "solicitacao_aprovada", "solicitacao_recusada"] },
  {
    id: "realizado", rotulo: "Realizado e matching",
    acoes: ["reconciliacao", "alocacao_confirmada", "alocacao_realocada", "alocacao_aplicada_aos_iguais", "status_corrigido",
            "competencia_ajustada_pela_descricao", "competencia_ajustada_manualmente", "competencia_mudou"],
  },
  { id: "acessos", rotulo: "Acessos", acoes: ["gestor_criado", "gestor_alterado"] },
  {
    id: "conta_azul", rotulo: "Conta Azul",
    acoes: ["credencial_criada", "credencial_rotacionada", "cadastros_atualizados_do_conta_azul"],
  },
];

const ROTULO_CAMPO: Record<string, string> = {
  exercicio_ano: "Exercício",
  valor_centavos: "Valor",
  competencia: "Competência",
  motivo: "Motivo",
  observacao: "Observação",
  versao_chave: "Versão da chave",
  rotacionado_em: "Renovado em",
};

function rotuloCampo(chave: string): string {
  if (ROTULO_CAMPO[chave]) return ROTULO_CAMPO[chave]!;
  const t = chave.replace(/_centavos$/, "").replaceAll("_", " ");
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function formatarValor(chave: string, v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  if (chave.endsWith("_centavos") && /^-?\d+$/.test(String(v))) return moeda(String(v));
  if (typeof v === "boolean") return v ? "sim" : "não";
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v)) return DATA_HORA_SP.format(new Date(v));
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

const ehObjeto = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

function quem(m: Movimentacao): ReactNode {
  // A identidade das integrações (migration 019) tem e-mail em `.invalid`.
  if (m.ator_email?.endsWith("@nexa.invalid")) return <span className="text-tinta-fraca">Sistema (automático)</span>;
  return m.ator_email ?? <span className="text-tinta-fraca">—</span>;
}

export function Historico() {
  const [grupo, setGrupo] = useState("todas");
  const [soPessoas, setSoPessoas] = useState(true);
  const [desde, setDesde] = useState("");
  const [ate, setAte] = useState("");
  const [aberta, setAberta] = useState<string | null>(null);

  const eu = useQuery({
    queryKey: ["eu"],
    queryFn: () => api.get<{ papeis: string[] }>("/auth/eu"),
    staleTime: 5 * 60_000,
  });
  // Só decide se o botão aparece. Quem recusa quem não é admin é a RPC.
  const ehAdmin = (eu.data?.papeis ?? []).includes("admin");

  const filtro: FiltroHistorico = {
    acoes: GRUPOS.find((g) => g.id === grupo)?.acoes,
    desde: desde || undefined,
    ate: ate || undefined,
    soPessoas,
  };

  const q = useInfiniteQuery({
    queryKey: ["historico", filtro],
    queryFn: ({ pageParam }) => buscarMovimentacoes(filtro, POR_PAGINA, pageParam),
    initialPageParam: 0,
    getNextPageParam: (ultima, todas) =>
      ultima.movimentacoes.length < POR_PAGINA ? undefined : todas.length * POR_PAGINA,
  });

  const itens = q.data?.pages.flatMap((p) => p.movimentacoes) ?? [];

  return (
    <div className="pagina pb-20">
      <header className="py-7">
        <p className="text-[11px] font-semibold tracking-[0.11em] text-tinta-fraca uppercase">Histórico</p>
        <h1 className="mt-1 font-display text-[22px] leading-tight font-bold tracking-tight">
          Histórico de movimentações
        </h1>
        <p className="mt-0.5 max-w-2xl text-[13.5px] text-tinta-media">
          Tudo o que muda no sistema fica registrado aqui: quem fez, quando, e como era antes e depois.
          Nenhum registro é apagado nem alterado — nem pelo sistema.
        </p>
      </header>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {GRUPOS.map((g) => (
          <Chip key={g.id} ativo={grupo === g.id} onClick={() => setGrupo(g.id)}>{g.rotulo}</Chip>
        ))}
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-4 text-[12.5px] text-tinta-media">
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={soPessoas} onChange={(e) => setSoPessoas(e.target.checked)} />
          só ações de pessoas (esconde as rotinas automáticas)
        </label>
        <label className="flex items-center gap-1.5">
          de
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)}
            className="rounded-lg border border-linha bg-superficie px-2 py-1 text-[12.5px]" />
        </label>
        <label className="flex items-center gap-1.5">
          até
          <input type="date" value={ate} onChange={(e) => setAte(e.target.value)}
            className="rounded-lg border border-linha bg-superficie px-2 py-1 text-[12.5px]" />
        </label>
        {(desde || ate) && (
          <button type="button" onClick={() => { setDesde(""); setAte(""); }} className="text-laranja underline">
            limpar datas
          </button>
        )}
      </div>

      {q.isPending && <p className="text-[13px] text-tinta-fraca">Carregando…</p>}
      {q.isError && <Vazio titulo="Não foi possível carregar o histórico" dica={(q.error as FalhaApi).message} />}

      {q.data && itens.length === 0 && (
        <Painel>
          <Vazio
            titulo="Nada registrado com esse filtro"
            dica={soPessoas
              ? "Desmarque “só ações de pessoas” para ver também as rotinas automáticas, como a sincronização da madrugada."
              : "Tente outro período ou outro tipo de ação."}
          />
        </Painel>
      )}

      {itens.length > 0 && (
        <div className="thin-scroll overflow-x-auto rounded-lg border border-linha bg-superficie">
          <table className="w-full border-collapse text-[13px]">
            <thead className="bg-superficie-2">
              <tr className="text-left text-[11px] font-semibold tracking-wide text-tinta-fraca uppercase">
                <th className="px-3 py-2">Quando</th>
                <th className="px-3 py-2">Quem</th>
                <th className="px-3 py-2">O que</th>
                <th className="px-3 py-2">Resumo</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {itens.map((m) => (
                <LinhaHistorico key={m.id} m={m} aberta={aberta === m.id} ehAdmin={ehAdmin}
                  onAlternar={() => setAberta(aberta === m.id ? null : m.id)} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {q.hasNextPage && (
        <div className="mt-4 flex justify-center">
          <button type="button" disabled={q.isFetchingNextPage} onClick={() => q.fetchNextPage()}
            className="rounded-lg border border-linha bg-superficie px-4 py-2 text-[13px] font-semibold text-tinta-media hover:bg-superficie-2 disabled:opacity-45">
            {q.isFetchingNextPage ? "Carregando…" : "Carregar mais"}
          </button>
        </div>
      )}
    </div>
  );
}

function LinhaHistorico({ m, aberta, ehAdmin, onAlternar }: {
  m: Movimentacao; aberta: boolean; ehAdmin: boolean; onAlternar: () => void;
}) {
  return (
    <>
      <tr className="border-t border-linha align-top">
        <td className="num px-3 py-2 whitespace-nowrap text-tinta-media">{DATA_HORA_SP.format(new Date(m.momento))}</td>
        <td className="px-3 py-2">{quem(m)}</td>
        <td className="px-3 py-2">
          <span className="font-semibold">{ROTULO_ACAO[m.acao] ?? m.acao}</span>
          {m.revertida_por && <span className="ml-2"><Pilula tom="neutro">revertida</Pilula></span>}
        </td>
        <td className="px-3 py-2 text-tinta-media">{resumo(m)}</td>
        <td className="px-3 py-2 text-right">
          <button type="button" onClick={onAlternar} aria-expanded={aberta}
            className="text-[12px] font-semibold text-laranja hover:underline">
            {aberta ? "fechar" : "detalhes"}
          </button>
        </td>
      </tr>
      {aberta && (
        <tr className="bg-superficie-2/60">
          <td colSpan={5} className="px-3 py-3">
            <Detalhe antes={m.antes} depois={m.depois} />
            {ehAdmin && m.reversivel && <Reverter m={m} />}
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Reversão, só admin. Sem janela de confirmação do navegador: o motivo é
 * obrigatório e digitado aqui, e é ele que faz as vezes de "tem certeza?".
 */
function Reverter({ m }: { m: Movimentacao }) {
  const qc = useQueryClient();
  const [abrindo, setAbrindo] = useState(false);
  const [motivo, setMotivo] = useState("");
  const reverter = useMutation({
    mutationFn: () => reverterMovimentacao(m.id, motivo.trim()),
    // A reversão mexe em pendência e em alocação, que aparecem em outras telas.
    onSuccess: () => qc.invalidateQueries(),
  });

  if (reverter.isSuccess) {
    return <p className="mt-3 text-[12.5px] font-semibold text-ok-tinta">Revertida. A reversão entrou no Histórico.</p>;
  }
  if (!abrindo) {
    return (
      <button type="button" onClick={() => setAbrindo(true)}
        className="mt-3 rounded-lg border border-linha bg-superficie px-3 py-1.5 text-[12.5px] font-semibold text-tinta-media hover:bg-superficie-2">
        Reverter esta ação
      </button>
    );
  }
  return (
    <div className="mt-3 grid max-w-xl gap-2 rounded-lg border border-atencao-linha bg-atencao-bg p-3 text-[12.5px]">
      <p className="text-atencao-tinta">
        {m.acao.startsWith("excecao")
          ? "A pendência volta ao estado em que estava antes desta ação."
          : "O lançamento volta para a alocação que tinha antes desta ação."}{" "}
        O registro original continua aqui, e a reversão entra como um registro novo.
      </p>
      <label className="grid gap-1">
        <span className="font-semibold text-tinta-media">Motivo (obrigatório)</span>
        <input value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={500}
          className="rounded-lg border border-linha bg-superficie px-2 py-1.5" />
      </label>
      {reverter.isError && <p className="text-risco-tinta">{(reverter.error as FalhaApi).message}</p>}
      <div className="flex gap-2">
        <button type="button" disabled={!motivo.trim() || reverter.isPending} onClick={() => reverter.mutate()}
          className="rounded-lg bg-laranja px-3 py-1.5 font-bold text-azul disabled:cursor-not-allowed disabled:opacity-45">
          {reverter.isPending ? "Revertendo…" : "Confirmar reversão"}
        </button>
        <button type="button" onClick={() => { setAbrindo(false); setMotivo(""); reverter.reset(); }}
          className="rounded-lg px-3 py-1.5 font-semibold text-tinta-media hover:bg-superficie">
          Cancelar
        </button>
      </div>
    </div>
  );
}

/** "1 linha" / "3 linhas": o resumo é lido de relance, e plural errado chama atenção. */
const qtd = (n: number | null, um: string, varios: string) => `${n ?? 0} ${(n ?? 0) === 1 ? um : varios}`;

/** Uma frase curta, sem abrir o detalhe. Só para as ações mais frequentes. */
function resumo(m: Movimentacao): string {
  const d = ehObjeto(m.depois) ? m.depois : {};
  const n = (k: string) => (typeof d[k] === "number" ? (d[k] as number) : null);
  switch (m.acao) {
    case "orcamento_importado":
      return `${qtd(n("aceitas"), "linha aceita", "linhas aceitas")}, ${qtd(n("rejeitadas"), "recusada", "recusadas")}`
        + (d.exercicio_ano ? ` · exercício ${d.exercicio_ano}` : "");
    case "orcamento_linhas_excluidas":
      return qtd(n("excluidas"), "linha excluída", "linhas excluídas") + (d.motivo ? ` · ${d.motivo}` : "");
    case "reconciliacao": {
      // Migration 037: quantas pendências a reconciliação fechou porque a causa acabou.
      const f = ehObjeto(d.pendencias_fechadas) ? d.pendencias_fechadas : {};
      const fechadas = (Number(f.nao_orcado) || 0) + (Number(f.estouro) || 0);
      return `${qtd(n("casados"), "lançamento casado", "lançamentos casados")}, ${qtd(n("alertas"), "alerta", "alertas")}`
        + (fechadas ? `, ${qtd(fechadas, "pendência fechada", "pendências fechadas")}` : "")
        + (typeof d.competencia === "string" ? ` · ${d.competencia.slice(0, 7)}` : "");
    }
    case "reversao": {
      const acao = typeof d.acao_revertida === "string" ? ROTULO_ACAO[d.acao_revertida] ?? d.acao_revertida : null;
      return [acao && `desfez: ${acao}`, typeof d.motivo_reversao === "string" && d.motivo_reversao].filter(Boolean).join(" · ");
    }
    case "alocacao_aplicada_aos_iguais": {
      // Migration 040: a lista inteira fica no detalhe; aqui, quantos e para onde.
      const aplicadas = Array.isArray(d.aplicadas) ? d.aplicadas.length : 0;
      const regras = Array.isArray(d.regras_criadas) ? d.regras_criadas.length : 0;
      return [`${qtd(aplicadas, "lançamento", "lançamentos")} em “${String(d.subcategoria ?? "")}”`,
              regras ? "lembrada para os próximos meses" : null].filter(Boolean).join(" · ");
    }
    case "excecoes_resolvidas_em_lote":
      return qtd(n("resolvidas"), "pendência", "pendências") + (d.observacao ? ` · ${d.observacao}` : "");
    case "subcategoria_renomeada": {
      // Migration 076: o nome de antes e o novo, e o exercício.
      const a = ehObjeto(m.antes) ? m.antes : {};
      return [`“${String(a.nome ?? "")}” → “${String(d.nome ?? "")}”`,
              d.exercicio ? `exercício ${d.exercicio}` : null,
              typeof d.motivo === "string" ? d.motivo : null].filter(Boolean).join(" · ");
    }
    default: {
      if (typeof d.motivo === "string") return d.motivo;
      if (typeof d.justificativa === "string") return d.justificativa;
      return "";
    }
  }
}

function Detalhe({ antes, depois }: { antes: unknown; depois: unknown }) {
  // Ação que atinge vários registros guarda a lista inteira em `antes`, para
  // o registro valer como cópia do que deixou de existir.
  const lista = Array.isArray(antes) ? antes : Array.isArray(depois) ? depois : null;
  const a = ehObjeto(antes) ? antes : {};
  const d = ehObjeto(depois) ? depois : {};
  const chaves = [...new Set([...Object.keys(a), ...Object.keys(d)])];

  return (
    <div className="grid gap-3">
      {chaves.length > 0 && (
        <table className="w-full max-w-3xl border-collapse text-[12.5px]">
          <thead>
            <tr className="text-left text-[11px] font-semibold tracking-wide text-tinta-fraca uppercase">
              <th className="py-1 pr-4">Campo</th>
              <th className="py-1 pr-4">Antes</th>
              <th className="py-1">Depois</th>
            </tr>
          </thead>
          <tbody>
            {chaves.map((k) => {
              const mudou = JSON.stringify(a[k]) !== JSON.stringify(d[k]);
              return (
                <tr key={k} className="border-t border-linha">
                  <td className="py-1 pr-4 text-tinta-media">{rotuloCampo(k)}</td>
                  <td className="num py-1 pr-4 break-all text-tinta-fraca">{formatarValor(k, a[k])}</td>
                  <td className={`num py-1 break-all ${mudou ? "font-semibold" : "text-tinta-fraca"}`}>{formatarValor(k, d[k])}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {lista && (
        <details className="text-[12.5px]">
          <summary className="cursor-pointer text-tinta-media">
            {lista.length} {lista.length === 1 ? "registro atingido" : "registros atingidos"} — ver a lista
          </summary>
          <pre className="thin-scroll mt-2 max-h-72 overflow-auto rounded-lg border border-linha bg-superficie p-2 text-[11.5px]">
            {JSON.stringify(lista.slice(0, 200), null, 2)}
            {lista.length > 200 && `\n… e mais ${lista.length - 200}`}
          </pre>
        </details>
      )}
      {chaves.length === 0 && !lista && <p className="text-[12.5px] text-tinta-fraca">Esta ação não gravou detalhe.</p>}
    </div>
  );
}

function Chip({ ativo, onClick, children }: { ativo: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" aria-pressed={ativo} onClick={onClick}
      className={`rounded-lg border px-3 py-1 text-[12.5px] font-semibold ${
        ativo ? "border-azul bg-azul text-white" : "border-linha bg-superficie text-tinta-media hover:bg-superficie-2"
      }`}>
      {children}
    </button>
  );
}
